import { Check, Save } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useTaxonomyContext } from '../contexts/TaxonomyContext';

import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/Dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useUpdateTaxonomy } from '@/hooks/useUpdateTaxonomy';
import { useUpdateVersion } from '@/hooks/useUpdateVersion';

export function TaxonomySelect() {
  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Taxonomy.components.TaxonomySelect',
  });

  const { updateTaxonomy } = useUpdateTaxonomy();
  const {
    taxonomyPropertiesUpdated,
    setTaxonomyPropertiesUpdated,
    setSelectedCategory,
    setSelectedVersion,
    selectedCategory,
    selectedVersion,
    mainVersion,
    setMainVersion,
    versions,
    sortedCategories,
  } = useTaxonomyContext();
  const { updateVersion } = useUpdateVersion();

  const effectiveVersion =
    selectedVersion ||
    mainVersion ||
    (versions ? versions[versions.length - 1] : '');

  const [isModalOpen, setIsModalOpen] = useState(false);

  const [isSaving, setIsSaving] = useState(false);

  async function setAsMainVersion() {
    try {
      await updateVersion({
        version: selectedVersion,
      });
      setMainVersion(selectedVersion);
    } catch {
      toast.error(t('toast.setAsMainVersionError'));
    }
  }

  function handleVersionChange(version: string) {
    setSelectedVersion(version);
    setSelectedCategory(sortedCategories?.[0] || '');
    setTaxonomyPropertiesUpdated(null);
  }

  async function saveChanges() {
    setIsSaving(true);
    setIsModalOpen(false);

    try {
      if (taxonomyPropertiesUpdated) {
        const data = await updateTaxonomy(taxonomyPropertiesUpdated);

        setSelectedVersion(data.version);

        toast.success(t('toast.saveChangesSuccess'));

        setTaxonomyPropertiesUpdated(null);
      }
    } catch (error) {
      toast.error(t('toast.saveChangesError'));
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <div className="mb-6 overflow-auto rounded-md border bg-white p-4">
      <div className="flex flex-col items-start gap-4 sm:flex-row sm:items-center">
        <div className="flex w-full flex-1 flex-col gap-5 sm:w-auto sm:flex-row">
          <div className="w-full sm:w-64">
            <Select
              value={effectiveVersion}
              onValueChange={handleVersionChange}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('select.versionPlaceholder')} />
              </SelectTrigger>

              <SelectContent>
                {(versions ?? [])
                  .slice()
                  .sort((a, b) =>
                    b.localeCompare(a, undefined, { numeric: true }),
                  )
                  .map((version) => (
                    <SelectItem key={version} value={version}>
                      {version}
                      {version === mainVersion && (
                        <Check className="ml-2 inline-block h-4 w-4 text-green-500" />
                      )}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>

            {mainVersion && (
              <div className="mt-2 text-left text-sm text-green-600">
                {t('currentMainVersionLabel')}
                {versions?.find((v) => v === mainVersion)}
              </div>
            )}
          </div>

          <div className="w-full sm:w-64">
            <Select
              value={selectedCategory || sortedCategories?.[0] || ''}
              onValueChange={setSelectedCategory}
              disabled={!selectedVersion}
            >
              <SelectTrigger>
                <SelectValue placeholder={t('select.categoryPlaceholder')} />
              </SelectTrigger>

              <SelectContent>
                {sortedCategories?.map((category) => (
                  <SelectItem key={category} value={category}>
                    {category}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex w-full flex-wrap justify-center gap-2 sm:justify-end">
            <Button
              onClick={setAsMainVersion}
              disabled={selectedVersion === mainVersion}
              className="w-auto"
            >
              {t('setAsMainVersionButton')}
            </Button>

            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
              <DialogTrigger asChild>
                <Button
                  variant="default"
                  className="w-auto"
                  disabled={
                    !taxonomyPropertiesUpdated ||
                    isSaving ||
                    !taxonomyPropertiesUpdated?.data?.length
                  }
                >
                  <Save className="h-4 w-4" />
                  {isSaving ? t('dialog.savingButton') : t('saveChangesButton')}
                </Button>
              </DialogTrigger>

              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('dialog.title')}</DialogTitle>

                  <DialogDescription>
                    {t('dialog.description')}
                  </DialogDescription>
                </DialogHeader>

                <DialogFooter className="flex justify-end gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIsModalOpen(false)}
                  >
                    {t('dialog.cancelButton')}
                  </Button>

                  <Button onClick={saveChanges} disabled={isSaving}>
                    {isSaving
                      ? t('dialog.savingButton')
                      : t('dialog.saveButton')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}
